import { Instagram, Youtube, Mail, MapPin } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { FOOTER } from "@/constants/testIds";

const TikTokIcon = ({ size = 18 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.05a8.16 8.16 0 0 0 4.77 1.5V7.1a4.85 4.85 0 0 1-1.84-.41Z"/>
  </svg>
);

const SOCIALS = [
  {
    key: "instagram",
    name: "Instagram",
    href: "https://www.instagram.com/boe.swiss/",
    Icon: Instagram,
    testId: FOOTER.instagram,
    handle: "@boe.swiss",
  },
  {
    key: "tiktok",
    name: "TikTok",
    href: "https://www.tiktok.com/@christianity.community",
    Icon: TikTokIcon,
    testId: FOOTER.tiktok,
    handle: "@christianity.community",
  },
  {
    key: "youtube",
    name: "YouTube",
    href: "https://www.youtube.com/watch?v=42OMwyU0KXY",
    Icon: Youtube,
    testId: FOOTER.youtube,
    handle: "YouTube",
  },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer className="relative bg-anthracite text-cream pt-20 pb-10 overflow-hidden">
      {/* Candle glow */}
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[300px]" style={{ background: "radial-gradient(ellipse, rgba(198,156,34,0.14) 0%, transparent 60%)" }} />
      <div className="pointer-events-none absolute -bottom-32 -left-32 w-96 h-96 rounded-full" style={{ background: "radial-gradient(circle, rgba(198,156,34,0.10) 0%, transparent 70%)" }} />

      <div className="container-boe relative">
        {/* Top welcome line */}
        <div className="text-center pb-16 border-b border-cream/10">
          <p className="font-accent italic text-2xl md:text-3xl max-w-3xl mx-auto leading-snug" style={{ color: "#E5C76B" }}>
            « {t.footer.welcome} »
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 py-14 border-b border-cream/10">
          {/* Brand */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-full bg-anthracite-soft border border-gold/40 flex items-center justify-center">
                <span className="font-accent text-gold text-2xl font-semibold leading-none" style={{ color: "#E5C76B" }}>B</span>
              </div>
              <div className="flex flex-col leading-tight">
                <span className="font-semibold text-cream tracking-tight">Brotherhood of Eden</span>
                <span className="text-[10px] uppercase tracking-[0.25em] text-cream/60">Schweiz</span>
              </div>
            </div>
            <p className="text-cream/75 max-w-sm leading-relaxed">{t.footer.tagline}</p>
            <p className="mt-4 text-xs text-cream/50 max-w-sm">{t.footer.notPolitical}</p>
          </div>

          {/* Locations */}
          <div className="lg:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.25em] mb-5 font-semibold" style={{ color: "#E5C76B" }}>
              {t.footer.locations}
            </h4>
            <ul className="space-y-3 text-cream/80 text-sm">
              <li className="flex items-start gap-2">
                <MapPin size={14} className="mt-0.5 flex-shrink-0" style={{ color: "#E5C76B" }} />
                <div>
                  <div className="text-cream font-medium">Bern</div>
                  <div className="text-cream/60 text-xs">{t.locations.bern.when}</div>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <MapPin size={14} className="mt-0.5 flex-shrink-0" style={{ color: "#E5C76B" }} />
                <div>
                  <div className="text-cream font-medium">Basel</div>
                  <div className="text-cream/60 text-xs">{t.locations.basel.when}</div>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <MapPin size={14} className="mt-0.5 flex-shrink-0" style={{ color: "#E5C76B" }} />
                <div>
                  <div className="text-cream font-medium">Lausanne</div>
                  <div className="text-cream/60 text-xs">{t.locations.lausanne.when}</div>
                </div>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.25em] mb-5 font-semibold" style={{ color: "#E5C76B" }}>
              {t.footer.contact}
            </h4>
            <button
              onClick={() => scrollTo("contact")}
              className="group inline-flex items-center gap-2 text-cream/80 hover:text-gold-soft transition-colors text-sm"
            >
              <Mail size={14} style={{ color: "#E5C76B" }} />
              <span className="underline-offset-4 group-hover:underline">{t.contact.eyebrow}</span>
            </button>
          </div>

          {/* Social */}
          <div className="lg:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.25em] mb-5 font-semibold" style={{ color: "#E5C76B" }}>
              {t.footer.follow}
            </h4>
            <ul className="space-y-3">
              {SOCIALS.map((s) => (
                <li key={s.key}>
                  <a
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={s.testId}
                    className="group inline-flex items-center gap-3 text-cream/80 hover:text-gold-soft transition-colors"
                  >
                    <span className="w-9 h-9 rounded-full border border-cream/20 group-hover:border-gold/60 flex items-center justify-center transition-colors">
                      <s.Icon size={16} />
                    </span>
                    <span className="text-sm">
                      <span className="block font-medium text-cream group-hover:text-gold-soft transition-colors">{s.name}</span>
                      <span className="block text-xs text-cream/60">{s.handle}</span>
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-cream/55">
          <span>© {new Date().getFullYear()} Brotherhood of Eden. {t.footer.rights}</span>
          <span className="font-accent italic" style={{ color: "#E5C76B" }}>
            « Bleibt in meiner Liebe. »
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
